import * as React from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import clsx from 'clsx';
import { useLocation } from '@reach/router';
import { render } from 'storyblok-rich-text-react-renderer';
import Video from '../video';
import FancyTitle from '../fancy-title';
import { extractLanguage, getStoryblokAlt } from '../../helpers/common';

/**
 * @param {string | undefined} filename
 * @param {{ width?: number, height?: number }} fallback
 */
function getStoryblokDimensions(filename, fallback = { width: undefined, height: undefined }) {
  const [rawWidth, rawHeight] = filename?.split('/')[5]?.split('x') || [];
  const width = Number.parseInt(rawWidth, 10);
  const height = Number.parseInt(rawHeight, 10);

  return {
    width: Number.isNaN(width) ? fallback.width : width,
    height: Number.isNaN(height) ? fallback.height : height,
  };
}

function getFocalPoint(url, focus) {
  if (!url || !focus || focus === '') {
    return { x: '50%', y: '50%' };
  }

  try {
    const urlParts = url.split('/');
    const sizePart = urlParts.find((part) => /^\d+x\d+$/.test(part));
    if (!sizePart) {
      return { x: '50%', y: '50%' };
    }

    const [imageWidth, imageHeight] = sizePart.split('x').map(Number);
    const focusPart = focus.split(':')[0];
    const [focalX, focalY] = focusPart.split('x').map(Number);

    if (!imageWidth || !imageHeight || Number.isNaN(focalX) || Number.isNaN(focalY)) {
      return { x: '50%', y: '50%' };
    }

    const percentageX = Math.round((focalX / imageWidth) * 100);
    const percentageY = Math.round((focalY / imageHeight) * 100);

    return {
      x: `${percentageX}%`,
      y: `${percentageY}%`,
    };
  } catch (error) {
    return { x: '50%', y: '50%' };
  }
}

function getImageSizeFromUrl(url) {
  if (!url) return null;

  const sizePart = url.split('/').find((part) => /^\d+x\d+$/.test(part));
  if (!sizePart) return null;

  const [width, height] = sizePart.split('x').map(Number);
  if (!width || !height) return null;

  return { width, height };
}

function getHeroImageFocalPoint(image) {
  const url = image?.filename;
  if (!url) return { x: '50%', y: '50%' };

  const rawFocus = image?.focus
    || image?.meta_data?.focus
    || image?.metadata?.focus
    || image?.focalPoint;

  if (!rawFocus) return { x: '50%', y: '50%' };

  if (typeof rawFocus === 'string') {
    return getFocalPoint(url, rawFocus);
  }

  if (typeof rawFocus === 'object' && rawFocus !== null) {
    const dimensions = getImageSizeFromUrl(url);
    if (!dimensions) return { x: '50%', y: '50%' };

    const x = Number(rawFocus.x);
    const y = Number(rawFocus.y);

    if (Number.isNaN(x) || Number.isNaN(y)) {
      return { x: '50%', y: '50%' };
    }

    const percentageX = Math.round((x / dimensions.width) * 100);
    const percentageY = Math.round((y / dimensions.height) * 100);

    return {
      x: `${Math.max(0, Math.min(100, percentageX))}%`,
      y: `${Math.max(0, Math.min(100, percentageY))}%`,
    };
  }

  return { x: '50%', y: '50%' };
}

/**
 * @param {string | undefined} url
 */
function getVideoMimeType(url) {
  if (!url) return 'video/mp4';
  return /\.webm(\?.*)?$/i.test(url) ? 'video/webm' : 'video/mp4';
}

/**
 * @param {{
 *   src: string,
 *   mobileSrc: string,
 *   desktopPoster: string | undefined,
 *   mobilePoster: string | undefined,
 *   preloadMode: 'none' | 'metadata' | 'auto',
 *   deferSrcUntilInView: boolean,
 *   deferSrcUntilPageLoad: boolean,
 *   deferSrcUntilInteraction: boolean,
 *   paused: boolean,
 *   onReady: () => void,
 * }} props
 */
function HeroVideo({
  src,
  mobileSrc,
  desktopPoster,
  mobilePoster,
  preloadMode,
  deferSrcUntilInView,
  deferSrcUntilPageLoad,
  deferSrcUntilInteraction,
  paused,
  onReady,
}) {
  const mobileSources = React.useMemo(() => (
    mobileSrc ? [{ src: mobileSrc, type: getVideoMimeType(mobileSrc) }] : []
  ), [mobileSrc]);

  const sources = React.useMemo(() => (
    src ? [{ src, type: getVideoMimeType(src) }] : []
  ), [src]);

  return (
    <div className="relative w-full h-full min-h-[400px] overflow-hidden">
      <Video
        mobileVideo={mobileSources}
        desktopVideo={sources}
        mobilePoster={mobilePoster}
        desktopPoster={desktopPoster}
        preloadMode={preloadMode}
        deferSrcUntilInView={deferSrcUntilInView}
        deferSrcUntilPageLoad={deferSrcUntilPageLoad}
        deferSrcUntilInteraction={deferSrcUntilInteraction}
        hideDesktopOnMobile={false}
        paused={paused}
        onReady={onReady}
      />
    </div>
  );
}

export default function Hero(blok) {
  const {
    title,
    subtitle,
    video,
    mobile_video: mobileVideoAsset,
    mobile_poster_image: mobilePosterImageAsset,
    hero_description,
    hero_badges,
  } = blok.blok;

  const styles = clsx('blok-hero');
  const location = useLocation();
  const currentLang = extractLanguage(location.pathname) === 'default' ? 'de' : 'en';
  const langUrl = currentLang === 'de' ? '' : `/${currentLang}`;
  const [isVideoReady, setIsVideoReady] = React.useState(false);
  const [isVideoPaused, setIsVideoPaused] = React.useState(false);
  const playbackLabels = currentLang === 'de'
    ? { play: 'Video abspielen', pause: 'Video pausieren' }
    : { play: 'Play video', pause: 'Pause video' };

  const editableAttrs = React.useMemo(() => storyblokEditable(blok), [blok?.blok?._uid]);
  const videoSrc = video?.filename || '';
  const mobileVideoSrc = mobileVideoAsset?.filename || videoSrc;
  const heroImage = blok?.blok?.hero_image || blok?.blok?.image;
  const heroImageFilename = heroImage?.filename || '';
  const heroImageFocalPoint = getHeroImageFocalPoint(heroImage);
  const isGifHeroImage = /\.gif(\?.*)?$/i.test(heroImageFilename);
  let heroImageUrl = '';
  let heroImageSmallMobileGifUrl = '';
  let heroImageMobileGifUrl = '';
  let heroImageDesktopGifUrl = '';
  let heroImageSmallMobileUrl = '';
  let heroImageMobileUrl = '';
  let heroImageDesktopUrl = '';
  if (heroImageFilename) {
    if (isGifHeroImage) {
      heroImageSmallMobileGifUrl = `${heroImageFilename}/m/720x0`;
      heroImageMobileGifUrl = `${heroImageFilename}/m/960x0`;
      heroImageDesktopGifUrl = `${heroImageFilename}/m/1920x0`;
      heroImageUrl = heroImageSmallMobileGifUrl;
    } else {
      heroImageSmallMobileUrl = `${heroImageFilename}/m/720x0/filters:format(webp)`;
      heroImageMobileUrl = `${heroImageFilename}/m/960x0/filters:format(webp)`;
      heroImageDesktopUrl = `${heroImageFilename}/m/1920x0/filters:format(webp)`;
      heroImageUrl = heroImageSmallMobileUrl;
    }
  }
  const desktopPosterAsset = blok?.blok?.image;
  const mobilePosterAsset = mobilePosterImageAsset || desktopPosterAsset;
  const posterUrl = desktopPosterAsset?.filename
    ? `${desktopPosterAsset.filename}/m/1920x0/filters:format(webp):quality(70)`
    : undefined;
  const mobilePosterUrl = mobilePosterAsset?.filename
    ? `${mobilePosterAsset.filename}/m/720x0/filters:format(webp):quality(70)`
    : undefined;
  const shouldUseVideoHero = Boolean(videoSrc);
  let heroMedia = (
    <HeroVideo
      src={videoSrc}
      mobileSrc={mobileVideoSrc}
      desktopPoster={posterUrl}
      mobilePoster={mobilePosterUrl}
      preloadMode="none"
      deferSrcUntilInView={false}
      deferSrcUntilPageLoad
      deferSrcUntilInteraction={false}
      paused={isVideoPaused}
      onReady={() => setIsVideoReady(true)}
    />
  );

  if (!shouldUseVideoHero && heroImageUrl) {
    heroMedia = isGifHeroImage ? (
      <picture>
        <source media="(max-width: 479px)" srcSet={heroImageSmallMobileGifUrl} />
        <source media="(max-width: 767px)" srcSet={heroImageMobileGifUrl} />
        <source media="(min-width: 768px)" srcSet={heroImageDesktopGifUrl} />
        <img
          src={heroImageUrl}
          alt={getStoryblokAlt(heroImage, heroImage?.title || title || '')}
          className="w-full h-full min-h-[50vh] md:min-h-0 object-cover"
          style={{ objectPosition: `${heroImageFocalPoint.x} ${heroImageFocalPoint.y}` }}
          width={getStoryblokDimensions(heroImageFilename).width}
          height={getStoryblokDimensions(heroImageFilename).height}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </picture>
    ) : (
      <picture>
        <source media="(max-width: 479px)" srcSet={heroImageSmallMobileUrl} />
        <source media="(max-width: 767px)" srcSet={heroImageMobileUrl} />
        <source media="(min-width: 768px)" srcSet={heroImageDesktopUrl} />
        <img
          src={heroImageUrl}
          srcSet={`${heroImageSmallMobileUrl} 720w, ${heroImageMobileUrl} 960w, ${heroImageDesktopUrl} 1920w`}
          sizes="(max-width: 479px) 100vw, (max-width: 767px) 100vw, 100vw"
          alt={getStoryblokAlt(heroImage, heroImage?.title || title || '')}
          className="w-full h-full min-h-[50vh] md:min-h-0 object-cover"
          style={{ objectPosition: `${heroImageFocalPoint.x} ${heroImageFocalPoint.y}` }}
          width={getStoryblokDimensions(heroImageFilename).width}
          height={getStoryblokDimensions(heroImageFilename).height}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </picture>
    );
  }

  return (
    <div {...editableAttrs} className={styles}>
      <div className="grid relative min-h-[62vh] md:min-h-auto">
        <a
          className="relative cursor-pointer"
          href={`${langUrl}/references`}
          target="_blank"
          rel="noreferrer"
          aria-label={
          langUrl === '/en'
            ? 'Open references in new tab'
            : 'Referenzen in neuem Tab öffnen'
          }
        >
          {heroMedia}
        </a>
        {shouldUseVideoHero && isVideoReady && (
          <button
            type="button"
            onClick={() => setIsVideoPaused((paused) => !paused)}
            aria-label={isVideoPaused ? playbackLabels.play : playbackLabels.pause}
            className="absolute top-4 right-4 md:top-auto md:bottom-8 md:right-8 z-30 flex items-center justify-center w-12 h-12 rounded-full bg-black/70 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="currentColor"
            >
              {isVideoPaused
                ? <path d="M8 5v14l11-7z" />
                : <path d="M6 5h4v14H6zM14 5h4v14h-4z" />}
            </svg>
          </button>
        )}
        <div className="absolute left-0 bottom-0">
          <div className="lg:m-8 p-4 lg:p-8 bg-white/50 backdrop-blur-md border border-white/30 text-black relative z-30">
            <FancyTitle
              text={title}
              tag="h1"
              fontSize="text-3xl lg:text-5xl"
              className="mb-4 lg:mb-7"
            />
            {hero_description && (
              <div className="mb-6 text-sm lg:text-base">
                {render(hero_description)}
              </div>
            )}
            {hero_badges && hero_badges.length > 0 && (
              <div className="flex items-center gap-20 mt-2">
                {hero_badges.map((badge) => (
                  (() => {
                    const { width, height } = getStoryblokDimensions(badge.filename);
                    return (
                      <img
                        key={badge.id}
                        src={badge.filename}
                        alt={getStoryblokAlt(badge, badge.title || '')}
                        width={width}
                        height={height}
                        className="h-8 lg:h-10 w-auto object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    );
                  })()
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
